import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    image: string;
    questions: EachQuestion[];
}

interface EachQuestion {
    question: string;
    answer: boolean;
}


const PictureComposition: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: boolean | null }>({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'PictureComposition.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/PictureComposition.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Picture Composition Worksheets for UKG | Creative Writing for Kids"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (questionIndex: number, selectedValue: boolean) => {
        setSelectedAnswers((prev) => {
            const updatedAnswers = { ...prev, [questionIndex]: selectedValue };
            checkAnswersAndProceed(updatedAnswers);
            return updatedAnswers;
        });
    };

    const checkAnswersAndProceed = (updatedAnswers: { [key: number]: boolean | null }) => {
        const currentQuestionSet = questions[currentIndex]?.questions;
        if (!currentQuestionSet) return;

        // Check if all answers are correct
        const allCorrect = currentQuestionSet.every((q, index) => updatedAnswers[index] === q.answer);
        if (allCorrect) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                if (currentIndex + 1 < questions.length) {
                    setCurrentIndex((prev) => prev + 1);
                }
                setShowImage(false);
                setSelectedAnswers({}); // Reset selections
            }, 3000);
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Enhance UKG students creativity and language development with fun picture composition exercises at Mama’s Guidance, encouraging sentence formation and storytelling skills.' />
            </Helmet>
            {showImage === false ? (
                <div className="seasons additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Picture Composition</h1>

                            <p>
                                <strong>Picture composition</strong> is a great way to develop your child&apos;s early writing, thinking, and observation skills. In these UKG worksheets, children look at a picture and create simple sentences or describe what they see. This activity <strong>encourages imagination, improves vocabulary, and builds confidence</strong> in sentence formation.
                            </p>

                            <h2>🖼️ What Is Picture Composition?</h2>
                            <p>
                                Picture composition is the process of looking at an image and writing meaningful sentences based on it. It helps children develop story ideas and improves their ability to express thoughts through words.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Observe and understand visual cues</li>
                                <li>Frame short and simple sentences</li>
                                <li>Improve <strong>grammar, vocabulary, and punctuation</strong></li>
                                <li>Develop logical thinking and creativity</li>
                            </ul>

                            <h2>🖍️ Activities in the Worksheets</h2>
                            <ul>
                                <li>Write 1–2 sentences about a picture</li>
                                <li>Choose correct sentence for a given image</li>
                                <li>Fill in the blanks based on the picture shown</li>
                                <li>Match images with sentences</li>
                                <li>Arrange sentences to match the story in the image</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Start with simple pictures like animals, fruits, or family</li>
                                <li>Ask open-ended questions like <strong>“What is the boy doing?” or “Where is the dog?”</strong></li>
                                <li>Encourage children to speak before writing</li>
                                <li>Use picture storybooks to inspire composition</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Let your child describe family photos or favorite cartoon scenes. Turn every image into a fun learning opportunity!
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Picture Composition</h1>

                        <p>
                            <strong>Picture composition</strong> is a great way to develop your child&apos;s early writing, thinking, and observation skills. In these UKG worksheets, children look at a picture and create simple sentences or describe what they see. This activity <strong>encourages imagination, improves vocabulary, and builds confidence</strong> in sentence formation.
                        </p>

                        <h2>🖼️ What Is Picture Composition?</h2>
                        <p>
                            Picture composition is the process of looking at an image and writing meaningful sentences based on it. It helps children develop story ideas and improves their ability to express thoughts through words.
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Observe and understand visual cues</li>
                            <li>Frame short and simple sentences</li>
                            <li>Improve <strong>grammar, vocabulary, and punctuation</strong></li>
                            <li>Develop logical thinking and creativity</li>
                        </ul>

                        <h2>🖍️ Activities in the Worksheets</h2>
                        <ul>
                            <li>Write 1–2 sentences about a picture</li>
                            <li>Choose correct sentence for a given image</li>
                            <li>Fill in the blanks based on the picture shown</li>
                            <li>Match images with sentences</li>
                            <li>Arrange sentences to match the story in the image</li>
                        </ul>

                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>Start with simple pictures like animals, fruits, or family</li>
                            <li>Ask open-ended questions like <strong>“What is the boy doing?” or “Where is the dog?”</strong></li>
                            <li>Encourage children to speak before writing</li>
                            <li>Use picture storybooks to inspire composition</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/english-language/articles"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Articles</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/english-language/one-and-many"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>One and Many</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/english-language/rhyming-words"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Rhyming Words</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            Let your child describe family photos or favorite cartoon scenes. Turn every image into a fun learning opportunity!
                        </p>
                    </article>
                    <div className="mt-4 mb-4 half-width">
                        <img className="img-thumbnail" src={process.env.REACT_APP_API_URL + questions[currentIndex]?.image} alt="Right Option" />
                    </div>
                    <div className="mb-5">
                        {questions[currentIndex]?.questions.map((item, index) => (
                            <div key={index}>
                                <h3>{index + 1}. {item.question}</h3>
                                <IonRadioGroup onIonChange={(e) => handleOptionClick(index, e.detail.value!)}>
                                    <IonItem className={`grid-item ${selectedAnswers[index] === true ? (true === item.answer ? "correct" : "errorItems") : ""}`}>
                                        <IonRadio slot="start" value={true} />
                                        <IonLabel>True</IonLabel>
                                    </IonItem>
                                    <IonItem className={`grid-item ${selectedAnswers[index] === false ? (false === item.answer ? "correct" : "errorItems") : ""}`}>
                                        <IonRadio slot="start" value={false} />
                                        <IonLabel>False</IonLabel>
                                    </IonItem>
                                </IonRadioGroup>
                            </div>
                        ))}
                        {error && <p className="error-message">Some answers are incorrect. Try again!</p>}
                    </div>
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} alt="Well Done" />
                </div>
            )}
        </>
    );
};

export default PictureComposition;
